import { fmtMoney, fmtDate, fileUrl, publicFileUrl, methodLabel } from "@/lib/api";
import { Logo, Money } from "@/components/common";

const PhotoSection = ({ label, photos, color, src }) => (
  <div className="mt-6" data-testid={`report-${label}-photos`}>
    <div className="flex items-center gap-2 mb-2"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded text-white ${color}`}>{label}</span><span className="text-sm font-semibold text-slate-700">{label === "before" ? "Before photos" : "After photos"} ({photos.length})</span></div>
    {photos.length === 0 ? <p className="text-sm text-slate-400">No {label} photos.</p> : (
      <div className="grid grid-cols-2 gap-2">
        {photos.map((p) => <img key={p.id} src={src(p.file_id)} alt={label} className="w-full aspect-[4/3] object-cover rounded-lg border" />)}
      </div>
    )}
  </div>
);

export const ReportView = ({ data, publicToken }) => {
  const { job, customer, business: biz, invoice: inv } = data;
  // Public report pages fetch assets through the token-bound public file route; owners use the authenticated route.
  const src = publicToken ? (id) => publicFileUrl(publicToken, id) : fileUrl;
  const logoSrc = biz?.logo_file_id ? src(biz.logo_file_id) : "";
  const before = job.photos.filter((p) => p.label === "before");
  const after = job.photos.filter((p) => p.label === "after");
  const addr = [biz?.address, biz?.city, [biz?.state, biz?.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const completed = (job.completed_at || job.scheduled_date || "").slice(0, 10);
  const methods = [...new Set((inv?.payments || []).map((p) => methodLabel(p.method)))];
  return (
    <div className="print-doc bg-white border border-slate-200 rounded-2xl shadow-sm p-5 sm:p-10 max-w-3xl mx-auto" data-testid="completion-report">
      <div className="flex flex-wrap justify-between gap-4 pb-6 border-b-2 border-orange-500">
        <div className="flex gap-3 items-start">
          {logoSrc ? <img src={logoSrc} alt={biz?.business_name} width={56} height={56} className="rounded-lg object-cover" style={{ width: 56, height: 56 }} /> : <Logo business={{ ...biz, logo_file_id: null }} size={56} />}
          <div>
            <div className="font-display text-2xl font-bold text-[#0B192C] leading-tight" data-testid="report-business-name">{biz?.business_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{addr}</div>
            <div className="text-xs text-slate-500">{[biz?.phone, biz?.email].filter(Boolean).join(" | ")}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold text-[#0B192C] tracking-wide">JOB REPORT</div>
          <div className="font-semibold text-slate-800" data-testid="report-job-number">{job.number}</div>
          <div className="text-xs text-slate-500">Completed {fmtDate(completed)}</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-6 py-6 text-sm">
        <div><div className="eyebrow mb-1">Customer</div><div className="font-semibold text-slate-900" data-testid="report-customer-name">{customer?.name}</div><div className="text-slate-600">{customer?.phone}</div><div className="text-slate-600">{customer?.email}</div></div>
        <div><div className="eyebrow mb-1">Job site</div><div className="text-slate-700">{job.job_address}</div><div className="text-slate-600 mt-1">Scheduled {fmtDate(job.scheduled_date)} {job.scheduled_time || ""}</div><div className="text-slate-600">Completed {fmtDate(completed)}</div></div>
      </div>
      <div className="eyebrow mb-1">Services performed</div>
      <div className="divide-y divide-slate-100 border-t border-b border-slate-200">
        {job.line_items.map((li) => <div key={li.id} className="flex justify-between py-2.5 text-sm"><span className="font-medium text-slate-900">{li.description}</span><Money value={li.total} /></div>)}
      </div>
      {job.notes && <div className="mt-5 text-sm"><div className="eyebrow mb-1">Job notes</div><p className="text-slate-700 whitespace-pre-line" data-testid="report-notes">{job.notes}</p></div>}
      {inv && (
        <div className="mt-6 rounded-xl bg-slate-50 border p-4 text-sm space-y-1.5" data-testid="report-payment-summary">
          <div className="flex justify-between text-slate-600"><span>Invoice {inv.number} total</span><Money value={inv.total} /></div>
          <div className="flex justify-between text-slate-600"><span>Amount paid</span><Money value={inv.amount_paid} /></div>
          <div className={`flex justify-between font-bold ${inv.balance_due > 0 ? "text-orange-600" : "text-emerald-700"}`}><span>{inv.balance_due > 0 ? "Remaining balance" : "Paid in full"}</span><Money value={inv.balance_due} /></div>
          {methods.length > 0 && <div className="flex justify-between text-slate-600 pt-1 border-t"><span>Payment method</span><span>{methods.join(", ")}</span></div>}
        </div>
      )}
      <PhotoSection label="before" photos={before} color="bg-orange-500" src={src} />
      <PhotoSection label="after" photos={after} color="bg-emerald-600" src={src} />
      <p className="mt-8 text-xs text-slate-400 text-center">Thank you for choosing {biz?.business_name}. We appreciate your business.</p>
    </div>
  );
};

export const fmtReportDate = fmtDate;
export const money = fmtMoney;
